import { useCallback, useEffect, useRef, useState } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useIsMultiDashboardsEnabled } from "resources/account"
import { useDashboardId } from "../context/dashboard-id"
import { tradeSlice, DEFAULT_TRADE_STATE } from "../store/trade-slice"

const PREFERENCE_SUBTYPE = "trade"
const PREFERENCE_FIELDS = ["isExpanded"]

export function useSyncTradePreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  // Instance mode: the workspace writer owns persistence, so suppress this subtype's write.
  const skipSave = useIsMultiDashboardsEnabled()

  // dashboardId must be stable for the hook's lifetime — usePreferences locks its baseline at mount.
  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncTradePreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime. "
        + "Unmount and remount DashboardLayout when navigating between dashboards, "
        + "or implement resetKey support in usePreferences.",
      )
    }
  }, [dashboardId])

  const state = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const setPreferencesAction = useCallback(
    (prefs: { isExpanded?: boolean }) =>
      tradeSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  // Returning to a dashboard remounts this subtree (key={dashboardId}) and re-runs the one-shot
  // load. When the slice slot was already populated at mount it holds the live value — possibly an
  // edit the debounced save hadn't flushed — so skip the load rather than clobber it with the
  // older cached read.
  const [wasInitializedAtMount] = useState(() => state.isPreferencesLoaded)

  const isLoaded = usePreferences(PREFERENCE_SUBTYPE, PREFERENCE_FIELDS, state, dispatch, setPreferencesAction, {
    skipInitialLoad: wasInitializedAtMount,
    skipSave,
  })

  // When the user has no saved preferences, usePreferences sets isLoaded=true without
  // dispatching setPreferencesAction (because preferencesToApply is {}). This leaves
  // s.dashboard.trade[dashboardId] undefined → isPreferencesLoaded stays false forever.
  useEffect(() => {
    if (isLoaded && !state.isPreferencesLoaded) {
      dispatch(tradeSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  // Return the slice flag, not usePreferences' internal flag — so the gate
  // stays closed until s.dashboard.trade[dashboardId] is actually populated
  // (otherwise consumers see DEFAULT_TRADE_STATE for a render).
  return state.isPreferencesLoaded
}
